<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import { fetchAdmins, createAdmin, updateAdmin } from "@/services/backofficeClient";
import { ROLES } from "@/services/acl";

const DEFAULT_PAGE_SIZE = 10;

const ROLE_GROUP_DEFS = [
  ["Dashboard", ["DASHBOARD_INDEX"]],
  ["Analysis", ["ANALYSIS_INDEX"]],
  ["Ads", ["ADS_INDEX", "ADS_CREATE", "ADS_EDIT", "ADS_DELETE"]],
  ["Admins", ["ADMIN_INDEX", "ADMIN_CREATE", "ADMIN_EDIT"]],
  ["Translation", ["TRANSLATION_INDEX"]],
  ["Chat", ["CHAT_INDEX"]],
  ["Complaints", ["COMPLAINT_INDEX", "COMPLAINT_EDIT"]],
  [
    "Compliance Rules",
    [
      "COMPLIANCE_RULE_INDEX",
      "COMPLIANCE_RULE_CREATE",
      "COMPLIANCE_RULE_EDIT",
      "COMPLIANCE_RULE_DELETE",
    ],
  ],
  ["Media", ["MEDIA_INDEX", "MEDIA_EDIT", "MEDIA_DELETE"]],
  [
    "Media Compliance Rules",
    [
      "MEDIA_COMPLIANCE_RULE_INDEX",
      "MEDIA_COMPLIANCE_RULE_CREATE",
      "MEDIA_COMPLIANCE_RULE_EDIT",
      "MEDIA_COMPLIANCE_RULE_DELETE",
      "BIOMETRIC_SEARCH",
    ],
  ],
  ["Pay", ["PAY_INDEX", "PAY_EDIT"]],
  ["Payment Methods", ["PAYMENT_METHOD_INDEX", "PAYMENT_METHOD_EDIT"]],
  ["Support", ["SUPPORT_INDEX", "SUPPORT_CREATE", "SUPPORT_EDIT"]],
  ["Users", ["USER_INDEX", "USER_IMPERSONATE", "USER_EDIT"]],
  ["Growth", ["GROWTH_INDEX"]],
  ["Verification", ["VERIFICATION_INDEX", "VERIFICATION_EDIT"]],
  [
    "Email Inbox",
    [
      "EMAIL_MANAGEMENT_INDEX",
      "EMAIL_MANAGEMENT_REPLY",
      "EMAIL_MANAGEMENT_DELETE",
      "EMAIL_MANAGEMENT_VIEW_ALL",
      "EMAIL_MANAGEMENT_ASSIGN",
    ],
  ],
  ["Config", ["CONFIG_INDEX", "CONFIG_EDIT"]],
];

const namedRoleValues = new Set([
  ROLES.SUPER_ADMIN,
  ...ROLE_GROUP_DEFS.flatMap(([, keys]) => keys.map((key) => ROLES[key])),
]);
const otherRoleValues = Object.values(ROLES).filter(
  (value) => !namedRoleValues.has(value)
);

const ROLE_GROUPS = [
  ...ROLE_GROUP_DEFS.map(([label, keys]) => ({
    label,
    roles: keys.map((key) => ROLES[key]),
  })),
  ...(otherRoleValues.length ? [{ label: "Other", roles: otherRoleValues }] : []),
];

const store = useStore();
const canCreateAdmin = computed(() => store.getters.hasRole(ROLES.ADMIN_CREATE));
const canEditAdmin = computed(() => store.getters.hasRole(ROLES.ADMIN_EDIT));
const isSuperAdmin = computed(() => store.getters.hasRole(ROLES.SUPER_ADMIN));

const admins = ref([]);
const searchTerm = ref("");
const isLoading = ref(false);
const errorMessage = ref("");
const currentPage = ref(1);
const pagination = ref({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNext: false,
});

const normalizedPageSize = computed(() => {
  const size = Number(pagination.value.pageSize);
  return Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;
});
const totalItems = computed(() => {
  const total = Number(pagination.value.totalItems);
  return Number.isFinite(total) && total >= 0 ? total : 0;
});
const totalPages = computed(() => {
  const pages = Number(pagination.value.totalPages);
  return Number.isFinite(pages) && pages > 0 ? pages : 1;
});
const rangeStart = computed(() => {
  if (totalItems.value === 0) return 0;
  return (Math.max(1, currentPage.value) - 1) * normalizedPageSize.value + 1;
});
const rangeEnd = computed(() => {
  if (totalItems.value === 0) return 0;
  return Math.min(currentPage.value * normalizedPageSize.value, totalItems.value);
});
const isEmpty = computed(
  () => !isLoading.value && !errorMessage.value && admins.value.length === 0
);
const canGoPrev = computed(() => currentPage.value > 1 && !isLoading.value);
const canGoNext = computed(
  () => currentPage.value < totalPages.value && !isLoading.value
);

const normalizeNumber = (value, fallback) => {
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : fallback;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseAdmins = (rawItems) => {
  if (Array.isArray(rawItems)) {
    return rawItems
      .filter((item) => item && typeof item === "object")
      .map((item) => {
        const lastLogin = item.lastLoginAt || item.last_login_at;
        const created = item.createdAt || item.created_at;
        return {
          id: item.id ? String(item.id) : "",
          name: item.name ? String(item.name) : "",
          email: item.email ? String(item.email) : "",
          roles: Array.isArray(item.roles)
            ? item.roles.map((role) => String(role))
            : [],
          lastLoginAt: parseDate(lastLogin),
          createdAt: parseDate(created),
        };
      });
  }

  return [];
};

const resolvePagination = (rawPagination, itemsLength) => {
  const fallbackTotalPages = Math.max(
    1,
    Math.ceil((itemsLength || 0) / DEFAULT_PAGE_SIZE)
  );

  if (rawPagination && typeof rawPagination === "object") {
    const page = normalizeNumber(rawPagination.page, currentPage.value);
    const pageSize = normalizeNumber(rawPagination.pageSize, DEFAULT_PAGE_SIZE);
    const totalItemsValue = normalizeNumber(
      rawPagination.totalItems,
      itemsLength
    );
    const totalPagesValue = normalizeNumber(
      rawPagination.totalPages,
      fallbackTotalPages
    );
    return {
      page: page > 0 ? page : 1,
      pageSize: pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
      totalItems: totalItemsValue >= 0 ? totalItemsValue : 0,
      totalPages: totalPagesValue > 0 ? totalPagesValue : fallbackTotalPages,
      hasNext:
        rawPagination.hasNext === true
          ? true
          : (page || 1) < (totalPagesValue || fallbackTotalPages),
    };
  }

  return {
    page: currentPage.value,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: itemsLength,
    totalPages: fallbackTotalPages,
    hasNext: fallbackTotalPages > currentPage.value,
  };
};

const loadAdmins = async () => {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    const searchValue =
      typeof searchTerm.value === "string" ? searchTerm.value.trim() : "";
    const payload = await fetchAdmins({
      page: currentPage.value,
      pageSize: DEFAULT_PAGE_SIZE,
      search: searchValue,
    });

    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    const parsedAdmins = parseAdmins(rawItems);
    const meta = resolvePagination(payload?.pagination, parsedAdmins.length);

    pagination.value = meta;
    currentPage.value = Math.min(
      Math.max(1, meta.page || 1),
      Math.max(1, meta.totalPages || 1)
    );
    admins.value = parsedAdmins;
  } catch (error) {
    console.error("Failed to fetch admins", error);
    errorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load administrators. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const submitSearch = () => {
  currentPage.value = 1;
  loadAdmins();
};

const refresh = () => {
  loadAdmins();
};

const resetFilters = () => {
  searchTerm.value = "";
  currentPage.value = 1;
  loadAdmins();
};

const changePage = (delta) => {
  const nextPage = currentPage.value + delta;
  if (nextPage < 1 || nextPage === currentPage.value) {
    return;
  }
  currentPage.value = nextPage;
  loadAdmins();
};

const formatRole = (value) => {
  if (!value) {
    return "Admin";
  }
  const clean = String(value).replace(/^role_/, "");
  return clean
    .split("_")
    .filter(Boolean)
    .map(
      (segment) =>
        segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
    )
    .join(" ");
};

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "--";
  }
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// ---------------------------------------------------------------------------
// Add / edit admin modal
// ---------------------------------------------------------------------------
const showModal = ref(false);
const editingAdminId = ref(null);
const editingAdminRoles = ref([]);
const modalSaving = ref(false);
const modalError = ref("");

const form = reactive({
  name: "",
  email: "",
  password: "",
  roles: [],
});

const isEditingModal = computed(() => Boolean(editingAdminId.value));
const targetIsSuperAdmin = computed(() =>
  editingAdminRoles.value.includes(ROLES.SUPER_ADMIN)
);
// Only a super admin may touch the role set of another super admin.
const canEditRoles = computed(
  () => isSuperAdmin.value || !targetIsSuperAdmin.value
);

const isRoleChecked = (role) => form.roles.includes(role);
const isRoleDisabled = (role) => {
  if (!canEditRoles.value) return true;
  if (role === ROLES.SUPER_ADMIN && !isSuperAdmin.value) return true;
  return false;
};
const toggleRole = (role) => {
  if (isRoleDisabled(role)) return;
  form.roles = isRoleChecked(role)
    ? form.roles.filter((value) => value !== role)
    : [...form.roles, role];
};

const openCreateModal = () => {
  editingAdminId.value = null;
  editingAdminRoles.value = [];
  form.name = "";
  form.email = "";
  form.password = "";
  form.roles = [];
  modalError.value = "";
  showModal.value = true;
};

const openEditModal = (admin) => {
  editingAdminId.value = admin.id;
  editingAdminRoles.value = [...(admin.roles || [])];
  form.name = admin.name || "";
  form.email = admin.email || "";
  form.password = "";
  form.roles = [...(admin.roles || [])];
  modalError.value = "";
  showModal.value = true;
};

const closeModal = () => {
  if (modalSaving.value) return;
  showModal.value = false;
};

const submitModal = async () => {
  modalError.value = "";
  const name = form.name.trim();
  const email = form.email.trim();
  const password = form.password.trim();

  if (!name) {
    modalError.value = "Name is required.";
    return;
  }
  if (!email) {
    modalError.value = "Email is required.";
    return;
  }

  if (!isEditingModal.value) {
    if (!password) {
      modalError.value = "Password is required.";
      return;
    }
    if (form.roles.length === 0) {
      modalError.value = "Select at least one role.";
      return;
    }
  }

  const payload = { name, email };
  if (password) payload.password = password;
  if (canEditRoles.value) payload.roles = form.roles;

  modalSaving.value = true;
  try {
    if (isEditingModal.value) {
      await updateAdmin(editingAdminId.value, payload);
    } else {
      await createAdmin(payload);
    }
    showModal.value = false;
    await loadAdmins();
  } catch (error) {
    console.error("Failed to save admin", error);
    modalError.value =
      error?.payload?.message || error?.message || "Failed to save admin.";
  } finally {
    modalSaving.value = false;
  }
};

onMounted(loadAdmins);
</script>
<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0">
            <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
              <div>
                <h6 class="mb-1">Admin directory</h6>
                <p class="mb-0 text-xs text-secondary">
                  Track who can sign into the backoffice.
                </p>
              </div>
              <div class="mt-3 mt-md-0 d-flex align-items-center gap-2 flex-wrap">
                <span class="badge bg-gradient-success text-uppercase text-xxs">
                  {{ totalItems }} total
                </span>
                <span class="text-xs text-secondary">
                  Page {{ currentPage }} of {{ totalPages }}
                </span>
                <button v-if="canCreateAdmin" type="button" class="btn btn-sm btn-success mb-0"
                  @click="openCreateModal">
                  <i class="fas fa-plus me-2"></i>
                  New admin
                </button>
              </div>
            </div>

            <form class="row g-2 align-items-center mt-3" @submit.prevent="submitSearch">
              <div class="col-12 col-md-7">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-white border-end-0">
                    <i class="ni ni-zoom-split-in"></i>
                  </span>
                  <input type="text" class="form-control border-start-0" placeholder="Search admins by any name or email"
                    v-model.trim="searchTerm" :disabled="isLoading" />
                </div>
              </div>
              <div class="col-auto">
                <button class="btn btn-sm btn-success mb-0" type="submit" :disabled="isLoading">
                  <i class="fas fa-search me-2"></i>
                  {{ isLoading ? "Searching..." : "Search" }}
                </button>
              </div>
              <div class="col-auto">
                <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="isLoading"
                  @click="resetFilters">
                  Reset filters
                </button>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-sm btn-outline-success mb-0" :disabled="isLoading"
                  @click="refresh">
                  <i class="fas fa-sync me-2"></i>
                  {{ isLoading ? "Refreshing..." : "Refresh" }}
                </button>
              </div>
            </form>
          </div>

          <div class="px-0 pt-0 pb-4 card-body">
            <div v-if="isLoading" class="text-center py-5">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 mb-0 text-sm text-secondary">
                Loading administrators...
              </p>
            </div>

            <div v-else-if="errorMessage" class="text-center py-5 px-4">
              <div class="icon icon-shape bg-gradient-danger shadow text-center border-radius-md text-white mx-auto">
                <i class="ni ni-fat-remove"></i>
              </div>
              <p class="mt-3 mb-2 text-sm text-dark">{{ errorMessage }}</p>
              <button type="button" class="btn btn-sm btn-success" @click="refresh">
                Try again
              </button>
            </div>

            <div v-else-if="isEmpty" class="text-center py-5 px-4">
              <div class="icon icon-shape bg-gradient-success shadow text-center border-radius-md text-white mx-auto">
                <i class="ni ni-key-25"></i>
              </div>
              <h6 class="mt-3">No administrators found</h6>
              <p class="text-sm text-secondary mb-3">
                Invite your teammates or adjust your filters to see admins here.
              </p>
              <button type="button" class="btn btn-sm btn-outline-success" @click="refresh">
                Reload
              </button>
            </div>

            <div v-else class="px-3">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Name
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                        Email
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                        Roles
                      </th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Last login
                      </th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Created
                      </th>
                      <th v-if="canEditAdmin" class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="admin in admins" :key="`${admin.id}-${admin.email}`">
                      <td>
                        <div class="d-flex flex-column">
                          <span class="text-sm font-weight-bold">
                            {{ admin.name || "--" }}
                          </span>
                          <span class="text-xxs text-secondary">
                            {{ admin.id || "" }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <p class="text-sm mb-0">{{ admin.email || "--" }}</p>
                      </td>
                      <td>
                        <div class="d-flex flex-wrap align-items-center" style="gap: 6px">
                          <span v-for="role in (admin.roles && admin.roles.length
                            ? admin.roles
                            : [''])" :key="`${admin.id}-${role || 'none'}`"
                            class="badge rounded-pill bg-light text-success border border-success text-xxs font-weight-bold px-3 py-2">
                            {{ role ? formatRole(role) : "--" }}
                          </span>
                        </div>
                      </td>
                      <td class="align-middle text-center">
                        <span class="text-sm">
                          {{ formatDate(admin.lastLoginAt) }}
                        </span>
                      </td>
                      <td class="align-middle text-center">
                        <span class="text-sm">
                          {{ formatDate(admin.createdAt) }}
                        </span>
                      </td>
                      <td v-if="canEditAdmin" class="align-middle text-center">
                        <button type="button" class="btn btn-sm btn-outline-secondary mb-0"
                          @click="openEditModal(admin)">
                          Edit
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="d-flex align-items-center flex-wrap gap-2 pt-3 border-top">
                <p class="mb-0 text-sm">
                  {{
                    totalItems === 0
                      ? "No results"
                      : `Showing ${rangeStart}-${rangeEnd} of ${totalItems}`
                  }}
                </p>
                <div class="ms-auto d-flex align-items-center gap-2">
                  <span class="text-xs text-secondary">
                    Page {{ currentPage }} of {{ totalPages }}
                  </span>
                  <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary" :disabled="!canGoPrev"
                      @click="changePage(-1)">
                      Previous
                    </button>
                    <button type="button" class="btn btn-outline-secondary" :disabled="!canGoNext"
                      @click="changePage(1)">
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showModal" class="admin-modal-backdrop" @click.self="closeModal">
        <div class="admin-modal-dialog card shadow-lg">
          <div class="card-header pb-2 d-flex align-items-center justify-content-between">
            <h6 class="mb-0">{{ isEditingModal ? "Edit admin" : "New admin" }}</h6>
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="modalSaving"
              @click="closeModal">
              ✕
            </button>
          </div>

          <div class="card-body">
            <div v-if="modalError" class="alert alert-danger text-sm py-2 mb-3">
              {{ modalError }}
            </div>

            <div class="mb-4">
              <label class="form-label text-sm fw-semibold">Name</label>
              <input v-model="form.name" type="text" class="form-control"
                placeholder="Display name" :disabled="modalSaving" />
            </div>

            <div class="mb-4">
              <label class="form-label text-sm fw-semibold">Email</label>
              <input v-model="form.email" type="email" class="form-control"
                placeholder="name@example.com" :disabled="modalSaving" />
            </div>

            <div class="mb-4">
              <label class="form-label text-sm fw-semibold">Password</label>
              <input v-model="form.password" type="password" class="form-control"
                :placeholder="isEditingModal ? 'Leave blank to keep current password' : 'Password'"
                :disabled="modalSaving" />
            </div>

            <div class="mb-2 d-flex align-items-center justify-content-between">
              <label class="form-label text-sm fw-semibold mb-0">Roles</label>
              <p v-if="!canEditRoles" class="text-xs text-danger mb-0">
                Only a super admin can change a super admin's roles.
              </p>
            </div>

            <div class="form-check mb-3 p-3 border rounded-3 bg-light">
              <input id="role-super-admin" class="form-check-input" type="checkbox"
                :checked="isRoleChecked(ROLES.SUPER_ADMIN)" :disabled="isRoleDisabled(ROLES.SUPER_ADMIN)"
                @change="toggleRole(ROLES.SUPER_ADMIN)" />
              <label class="form-check-label text-sm fw-bold" for="role-super-admin">
                Super admin
              </label>
              <p class="text-xs text-secondary mb-0">
                Grants every permission below automatically.
              </p>
            </div>

            <fieldset :disabled="!canEditRoles" class="row g-3 admin-roles-grid">
              <div v-for="group in ROLE_GROUPS" :key="group.label" class="col-6 col-md-4">
                <p class="text-xxs text-uppercase text-secondary font-weight-bolder mb-1">
                  {{ group.label }}
                </p>
                <div v-for="role in group.roles" :key="role" class="form-check">
                  <input :id="`role-${role}`" class="form-check-input" type="checkbox"
                    :checked="isRoleChecked(role)" :disabled="isRoleDisabled(role)" @change="toggleRole(role)" />
                  <label class="form-check-label text-sm" :for="`role-${role}`">
                    {{ formatRole(role) }}
                  </label>
                </div>
              </div>
            </fieldset>

            <div class="d-flex align-items-center gap-2 mt-4">
              <button type="button" class="btn btn-success mb-0" :disabled="modalSaving" @click="submitModal">
                {{ modalSaving ? "Saving..." : isEditingModal ? "Save changes" : "Create admin" }}
              </button>
              <button type="button" class="btn btn-outline-secondary mb-0" :disabled="modalSaving"
                @click="closeModal">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.admin-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.admin-modal-dialog {
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
}

.admin-roles-grid {
  max-height: 320px;
  overflow-y: auto;
}
</style>
